import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { playNotificationSound } from '../../services/notificationSound';

const POLL_INTERVAL = 3000;

export default function ChatPanel({ chatUser, onClose, currentUserRole }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const messagesEndRef = useRef(null);
  const fileRef = useRef(null);
  const prevMsgCountRef = useRef(0);
  const initialLoadRef = useRef(true);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversation = useCallback(async () => {
    try {
      const res = await api.get(`/messages/conversation/${chatUser.id}/`);
      const newMessages = res.data;

      // Play sound if we received new incoming messages (not on initial load)
      if (!initialLoadRef.current && newMessages.length > prevMsgCountRef.current) {
        const latestNew = newMessages.slice(prevMsgCountRef.current);
        const hasIncoming = latestNew.some(m => m.is_incoming);
        if (hasIncoming) {
          playNotificationSound();
        }
      }
      initialLoadRef.current = false;
      prevMsgCountRef.current = newMessages.length;
      setMessages(newMessages);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [chatUser.id]);

  useEffect(() => {
    setMessages([]);
    setLoading(true);
    prevMsgCountRef.current = 0;
    initialLoadRef.current = true;
    fetchConversation();
    const interval = setInterval(fetchConversation, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);

    const msgText = text.trim();

    // Optimistic update: immediately show the sent message
    const optimisticMsg = {
      id: `temp-${Date.now()}`,
      message: msgText,
      is_incoming: false,
      created_at: new Date().toISOString(),
      attachment: null,
      subject: 'Chat',
    };
    setMessages(prev => [...prev, optimisticMsg]);
    prevMsgCountRef.current += 1;
    setText('');

    try {
      const data = new FormData();
      data.append('message', msgText);
      data.append('recipient_user_id', chatUser.id);
      if (attachment) data.append('attachment', attachment);

      await api.post('/messages/chat/send/', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAttachment(null);
      if (fileRef.current) fileRef.current.value = '';
      // Immediately fetch to get authoritative server data
      fetchConversation();
    } catch {
      // Revert optimistic update on failure
      setMessages(prev => prev.filter(m => m.id !== optimisticMsg.id));
      prevMsgCountRef.current -= 1;
      setText(msgText);
      toast.error('Failed to send message.');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (d) => {
    const date = new Date(d);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
      ' ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const handleEdit = async (msgId) => {
    const trimmed = editText.trim();
    if (!trimmed) return;
    try {
      await api.patch(`/messages/chat/${msgId}/edit/`, { message: trimmed });
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, message: trimmed, is_edited: true } : m));
      setEditingId(null);
      setEditText('');
    } catch {
      toast.error('Failed to edit message.');
    }
  };

  const handleDelete = async (msgId) => {
    try {
      await api.delete(`/messages/chat/${msgId}/delete/`);
      setMessages(prev => prev.filter(m => m.id !== msgId));
      prevMsgCountRef.current -= 1;
      setDeleteConfirmId(null);
    } catch {
      toast.error('Failed to delete message.');
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    if (!menuOpenId) return;
    const handler = () => setMenuOpenId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [menuOpenId]);

  return (
    <div className="flex flex-col h-full bg-white rounded-xl shadow-sm border overflow-hidden">
      {/* Chat Header */}
      <div className="bg-gradient-to-r from-[#062E10] to-[#0B5D1E] px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="relative">
            {chatUser.photo ? (
              <img src={chatUser.photo} alt="" className="w-9 h-9 rounded-full object-cover border-2 border-white/30" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-sm">
                {(chatUser.name || '?').charAt(0).toUpperCase()}
              </div>
            )}
            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-[#062E10]" />
          </div>
          <div>
            <p className="text-white font-semibold text-sm">{chatUser.name}</p>
            {chatUser.membership_number && (
              <p className="text-white/60 text-[11px]">NIS/ {chatUser.membership_number}</p>
            )}
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-white/70 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition"
          title="Close chat"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-[#F8FAFB]">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="w-7 h-7 border-3 border-[#0B5D1E]/20 border-t-[#0B5D1E] rounded-full animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-3">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <p className="text-sm font-medium">No messages yet</p>
            <p className="text-xs mt-1">Send a message to start a conversation</p>
          </div>
        ) : (
          <>
            {messages.map((msg, idx) => {
              const isMe = !msg.is_incoming;
              const isTemp = String(msg.id).startsWith('temp-');
              const showDate = idx === 0 || new Date(msg.created_at).toDateString() !== new Date(messages[idx - 1].created_at).toDateString();
              return (
                <React.Fragment key={msg.id}>
                  {showDate && (
                    <div className="flex justify-center my-2">
                      <span className="text-[10px] bg-gray-200 text-gray-500 px-3 py-1 rounded-full font-medium">
                        {new Date(msg.created_at).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                  )}
                  <div className={`flex ${isMe ? 'justify-end' : 'justify-start'} group`}>
                    <div className={`max-w-[75%] relative ${isMe ? 'order-1' : ''}`}>
                      {/* Delete confirmation overlay */}
                      {deleteConfirmId === msg.id && (
                        <div className="absolute inset-0 z-10 bg-white/95 rounded-2xl flex flex-col items-center justify-center gap-2 border border-red-200 shadow-lg p-3">
                          <p className="text-xs text-gray-600 font-medium">Delete this message?</p>
                          <div className="flex gap-2">
                            <button onClick={() => handleDelete(msg.id)}
                              className="px-3 py-1 bg-red-500 text-white text-xs rounded-lg hover:bg-red-600 font-medium">
                              Delete
                            </button>
                            <button onClick={() => setDeleteConfirmId(null)}
                              className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-lg hover:bg-gray-200 font-medium">
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Three-dot menu for own messages */}
                      {isMe && !isTemp && (
                        <div className={`absolute -left-7 top-1 z-10 ${menuOpenId === msg.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
                          <button
                            onClick={(e) => { e.stopPropagation(); setMenuOpenId(menuOpenId === msg.id ? null : msg.id); }}
                            className="p-0.5 rounded-full hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition"
                            title="Options"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zm0 6a2 2 0 110-4 2 2 0 010 4zm0 6a2 2 0 110-4 2 2 0 010 4z" />
                            </svg>
                          </button>
                          {menuOpenId === msg.id && (
                            <div className="absolute right-0 top-6 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[100px] z-20">
                              <button
                                onClick={(e) => { e.stopPropagation(); setEditingId(msg.id); setEditText(msg.message); setMenuOpenId(null); }}
                                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 transition"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Edit
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(msg.id); setMenuOpenId(null); }}
                                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 transition"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      <div className={`rounded-2xl px-4 py-2.5 ${
                        isMe
                          ? 'bg-[#0B5D1E] text-white rounded-br-md'
                          : 'bg-white text-gray-800 border border-gray-100 shadow-sm rounded-bl-md'
                      }`}>
                        {msg.subject && msg.subject !== 'Chat' && (
                          <p className={`text-xs font-semibold mb-1 ${isMe ? 'text-white/80' : 'text-gray-500'}`}>
                            {msg.subject}
                          </p>
                        )}
                        {editingId === msg.id ? (
                          <div className="space-y-2">
                            <textarea
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleEdit(msg.id); }
                                if (e.key === 'Escape') { setEditingId(null); setEditText(''); }
                              }}
                              className="w-full px-2 py-1.5 rounded-lg text-sm text-gray-900 border border-white/30 outline-none resize-none bg-white/95"
                              rows={2}
                              autoFocus
                            />
                            <div className="flex gap-1.5 justify-end">
                              <button onClick={() => { setEditingId(null); setEditText(''); }}
                                className="px-2 py-0.5 text-[10px] rounded bg-white/20 text-white hover:bg-white/30 font-medium">
                                Cancel
                              </button>
                              <button onClick={() => handleEdit(msg.id)}
                                disabled={!editText.trim()}
                                className="px-2 py-0.5 text-[10px] rounded bg-white text-[#0B5D1E] hover:bg-white/90 font-semibold disabled:opacity-50">
                                Save
                              </button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.message}</p>
                        )}
                        {msg.attachment && (
                          <a href={msg.attachment} target="_blank" rel="noopener noreferrer"
                            className={`inline-flex items-center gap-1.5 mt-2 text-xs font-medium ${
                              isMe ? 'text-white/80 hover:text-white' : 'text-[#0B5D1E] hover:underline'
                            }`}>
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                            </svg>
                            Attachment
                          </a>
                        )}
                      </div>
                      <p className={`text-[10px] text-gray-400 mt-1 px-1 ${isMe ? 'text-right' : ''}`}>
                        {formatTime(msg.created_at)}
                        {msg.is_edited && <span className="ml-1 italic">· edited</span>}
                      </p>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <form onSubmit={handleSend} className="border-t border-gray-100 px-3 py-2.5 bg-white flex-shrink-0">
        {attachment && (
          <div className="flex items-center gap-2 mb-2 bg-gray-50 rounded-lg px-3 py-2 border text-sm">
            <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
            </svg>
            <span className="text-xs text-gray-600 truncate flex-1">{attachment.name}</span>
            <button type="button" onClick={() => { setAttachment(null); if (fileRef.current) fileRef.current.value = ''; }}
              className="text-red-400 hover:text-red-600 p-0.5 flex-shrink-0">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
        <div className="flex items-end gap-2">
          {/* Attachment button */}
          <label className="p-2 text-gray-400 hover:text-[#0B5D1E] cursor-pointer transition flex-shrink-0 rounded-lg hover:bg-gray-50">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
            </svg>
            <input ref={fileRef} type="file" className="hidden" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
              onChange={(e) => {
                const f = e.target.files[0];
                if (f && f.size > 10 * 1024 * 1024) { toast.error('Max 10MB'); return; }
                setAttachment(f || null);
              }} />
          </label>

          {/* Text input */}
          <div className="flex-1 relative">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend(e);
                }
              }}
              placeholder={`Message ${chatUser.name}...`}
              rows={1}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#0B5D1E]/30 focus:border-[#0B5D1E] outline-none resize-none text-sm max-h-24 overflow-y-auto"
              style={{ minHeight: '42px' }}
            />
          </div>

          {/* Send button */}
          <button
            type="submit"
            disabled={sending || !text.trim()}
            className="p-2.5 bg-[#0B5D1E] text-white rounded-xl hover:bg-[#062E10] transition disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
          >
            {sending ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
